import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  TextField, 
  Radio, 
  RadioGroup,
  FormControlLabel, 
  FormControl, 
  Button, 
  Stepper, 
  Step, 
  StepLabel,
  Paper,
  Grid,
  MenuItem,
  Select,
  Checkbox,
  FormGroup,
  Divider,
  Alert,
  InputLabel,
  LinearProgress,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de } from 'date-fns/locale';
import { useParams } from 'react-router-dom';
import api from '../config/api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { PictureAsPdf as PdfIcon } from '@mui/icons-material';

// Bewertungsskala für die Auswahl
const bewertungsSkala = [
  { wert: 1, label: 'Stimme gar nicht zu' },
  { wert: 2, label: 'Stimme nicht zu' },
  { wert: 3, label: 'Teils/Teils' },
  { wert: 4, label: 'Stimme zu' },
  { wert: 5, label: 'Stimme voll zu' }
];

const OSPFragebogen = () => {
  const { uuid, sessionId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    personendaten: {
      name: '',
      datum: new Date(),
      geschlecht: '',
      diagnose: '',
      tragezeitVon: null,
      tragezeitBis: null,
      anzahlPlatten: 1,
      plattenNummer: 1,
      gesamtentwicklung: '',
      handhabung: '',
      anwendungAlltag: ''
    },
    gruende: {
      offeneMundhaltung: false,
      mundatmung: false,
      schnarchen: false,
      zaehneKnirschen: false,
      schwacheLippen: false,
      verkuerzteOberlippe: false,
      wulstigeUnterlippe: false,
      raueLippen: false,
      undeutlichesSprechen: false,
      naeseln: false,
      lautfehlbildungen: false,
      zahnKieferfehlstellungen: false
    },
    angewohnheiten: {
      daumenlutschen: false,
      schnullergebrauch: false,
      fingernaegel: false,
      lippenbeissen: false,
      saugenZunge: false,
      saugenWangen: false
    },
    lippeneinzug: {
      unterlippe: false,
      oberlippe: false,
      lippenbeissen: false
    },
    zungenposition: {
      zungenTieflage: false,
      zungenEinlagerungUkOk: false,
      zungenEinlagerungLippen: false,
      zungendruckSchluckenOk: false,
      zungendruckOkUk: false,
      zwischenLippen: false,
      zungendruckSprechenUk: false,
      zungendruckOkLippen: false,
      gegenUk: false,
      zwischenOkUk: false,
      zwischenLippenSprechen: false
    },
    nahrungsaufnahme: {
      babyflasche: false,
      trinkenStrohalm: false,
      breiigeNahrung: false,
      weichereNahrung: false
    },
    zungenbewegung: {
      sichtbar: false,
      schmatzen: false,
      nahrungAustritt: false
    },
    artikulationsmerkmale: {
      stimmklangNasal: false,
      stimmklangHeiser: false,
      insgesamtUndeutlich: false
    },
    verbesserungen: {
      // Wird dynamisch aus den ausgewählten Gründen generiert
    },
    gesamtentwicklung: '',
    handhabung: '',
    anwendungAlltag: ''
  });
  
  const [verbesserungsOptionen, setVerbesserungsOptionen] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [sessionStatus, setSessionStatus] = useState(null);
  const [completedData, setCompletedData] = useState(null);
  const [instituteConfig, setInstituteConfig] = useState(null);

  // Schritte des Fragebogens
  const steps = [
    'Persönliche Daten',
    'Gründe für OSP',
    'Angewohnheiten & Lippeneinzug',
    'Zungenposition',
    'Nahrungsaufnahme & Zungenbewegung',
    'Artikulation',
    'Verbesserungen',
    'Abschluss'
  ];

  // Load institute configuration
  useEffect(() => {
    const loadInstituteConfig = async () => {
      try {
        const response = await api.get('/api/institute');
        setInstituteConfig(response.data);
      } catch (err) {
        console.warn('Could not load institute configuration:', err);
        // Fallback to correct values matching backend config
        setInstituteConfig({
          name: 'ISST',
          full_name: 'Institut für systemische Stimm- und Sprachtherapie',
          subtitle: 'Unna',
          description: 'Internationales Zentrum für Rehabilitation',
          owner: 'Sabine Berndsen',
          scientific_lead: 'Dr. Klaus-Jürgen Berndsen'
        });
      }
    };

    loadInstituteConfig();
  }, []);

  // UUID und Session aus der URL laden und bei der Initialisierung prüfen
  useEffect(() => {
    const validateSession = async () => {
      try {
        const response = await api.get(`/api/sessions/validate/${uuid}/${sessionId}`);

        if (!response.data.valid) {
          // Session ist nicht mehr gültig
          setSessionStatus(response.data.status);
          setError(response.data.message);

          // Wenn Fragebogen bereits ausgefüllt wurde, lade die Daten für PDF-Generierung
          if (response.data.status === 'completed') {
            try {
              const questionnaireResponse = await api.get(`/api/questionnaires/${uuid}/${sessionId}`);
              if (questionnaireResponse.data.exists) {
                setCompletedData(questionnaireResponse.data.data);
                setFormData(questionnaireResponse.data.data);
              }
            } catch (err) {
              console.warn('Konnte abgeschlossene Fragebogendaten nicht laden:', err);
            }
          }
          return;
        }

        if (response.data.existingData) {
          // Vorhandene Daten laden, falls der Fragebogen bereits teilweise ausgefüllt wurde
          setFormData(response.data.formData);
        }
      } catch (err) {
        setError('Die Sitzung ist ungültig oder konnte nicht überprüft werden. Bitte fordern Sie einen neuen Link an.');
      }
    };

    if (uuid && sessionId) {
      validateSession();
    }
  }, [uuid, sessionId]);

  // Verbesserungsoptionen basierend auf ausgewählten Gründen generieren
  useEffect(() => {
    const selectedGruende = Object.entries(formData.gruende)
      .filter(([_, isSelected]) => isSelected)
      .map(([key, _]) => {
        switch(key) {
          case 'offeneMundhaltung': return 'Offene Mundhaltung';
          case 'mundatmung': return 'Mundatmung';
          case 'schnarchen': return 'Schnarchen';
          case 'zaehneKnirschen': return 'Zähneknirschen';
          case 'schwacheLippen': return 'Schwache Lippen';
          case 'verkuerzteOberlippe': return 'Verkürzte Oberlippe';
          case 'wulstigeUnterlippe': return 'Wulstige Unterlippe';
          case 'raueLippen': return 'Raue, rissige Lippen';
          case 'undeutlichesSprechen': return 'Undeutliches Sprechen';
          case 'naeseln': return 'Näseln';
          case 'lautfehlbildungen': return 'Lautfehlbildungen';
          case 'zahnKieferfehlstellungen': return 'Zahn- und/oder Kieferfehlstellungen/Fehlbildungen';
          default: return '';
        }
      })
      .filter(label => label !== '');
    
    const newVerbesserungen = {};
    selectedGruende.forEach(grund => {
      newVerbesserungen[grund] = 0;
    });
    
    setVerbesserungsOptionen(selectedGruende);
    setFormData(prev => ({
      ...prev,
      verbesserungen: newVerbesserungen
    }));
  }, [formData.gruende]);

  // PDF Download für Patienten
  const handlePatientPDFDownload = async () => {
    try {
      console.log('PDF Download gestartet...');
      console.log('Completed Data:', completedData);

      const printContent = document.getElementById('completed-questionnaire-content');
      if (!printContent) {
        console.error('Element completed-questionnaire-content nicht gefunden');
        console.log('Verfügbare Elemente:', document.body.innerHTML.substring(0, 500));
        alert('PDF-Element nicht gefunden. Bitte versuchen Sie es erneut.');
        return;
      }

      // Stelle sicher, dass das Element sichtbar ist
      if (printContent.offsetParent === null) {
        console.error('PDF-Element ist nicht sichtbar');
        alert('PDF-Element ist nicht sichtbar. Bitte laden Sie die Seite neu.');
        return;
      }

      console.log('Starte PDF-Generierung für Patienten-Ansicht...');

      // Temporär das Element für bessere PDF-Generierung anpassen
      const originalStyle = printContent.style.cssText;
      printContent.style.width = '800px';
      printContent.style.padding = '20px';
      printContent.style.backgroundColor = 'white';

      // Erstelle Canvas vom HTML-Inhalt
      console.log('Erstelle Canvas...');
      const canvas = await html2canvas(printContent, {
        scale: 2,
        useCORS: true,
        logging: false,
        windowWidth: 850,
        scrollY: -window.scrollY,
        backgroundColor: '#ffffff'
      });

      console.log('Canvas erstellt, Größe:', canvas.width, 'x', canvas.height);

      // Stelle Original-Style wieder her
      printContent.style.cssText = originalStyle;

      // Erstelle PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const margin = 10;
      const imgWidth = 210 - (2 * margin);
      const pageHeight = 297 - (2 * margin);
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = margin;

      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight + margin;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Speichere das PDF
      const patientName = formData.personendaten.name || 'Patient';
      const fileName = `Mein_OSP_Fragebogen_${patientName.replace(/[^a-zA-Z0-9äöüÄÖÜß]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;

      console.log('Speichere PDF als:', fileName);
      pdf.save(fileName);

      console.log('PDF erfolgreich erstellt und heruntergeladen');
    } catch (error) {
      console.error('Fehler beim Erstellen des PDFs:', error);
      alert('Fehler beim Erstellen des PDFs: ' + error.message);
    }
  };

  // Render completed questionnaire for PDF
  const renderCompletedQuestionnaire = () => {
    if (!completedData || !completedData.personendaten) {
      return <Typography>Keine Fragebogendaten verfügbar</Typography>;
    }

    return (
      <Box id="completed-questionnaire-content">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4, borderBottom: '2px solid #333', pb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            {instituteConfig ? `${instituteConfig.name} - ${instituteConfig.full_name}` : 'ISST - Institut für systemische Stimm- und Sprachtherapie'}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            OSP-Fragebogen - Ausgefüllte Antworten
          </Typography>
        </Box>

        {/* Patient Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>Persönliche Daten</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography><strong>Name:</strong> {completedData.personendaten.name || '-'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography><strong>Geschlecht:</strong> {completedData.personendaten.geschlecht || '-'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography><strong>Diagnose:</strong> {completedData.personendaten.diagnose || '-'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography><strong>Tragezeit:</strong> {completedData.personendaten.tragezeitVon || '-'} bis {completedData.personendaten.tragezeitBis || '-'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography><strong>Anzahl Stimulationsplatten:</strong> {completedData.personendaten.anzahlStimulationsplatten || '-'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography><strong>Wievielte Platte:</strong> {completedData.personendaten.wievielteStimulationsplatte || '-'}</Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Selected Reasons */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>Gründe für den Einsatz einer OSP</Typography>
          {Object.entries(completedData.gruende || {}).filter(([_, selected]) => selected).map(([key, _]) => {
            // Complete and consistent labels mapping
            const getLabel = (fieldKey) => {
              const labels = {
                offeneMundhaltung: 'Offene Mundhaltung',
                mundatmung: 'Mundatmung',
                schnarchen: 'Schnarchen',
                zaehneKnirschen: 'Zähneknirschen',
                schwacheLippen: 'Schwache Lippen',
                verkuerzteOberlippe: 'Verkürzte Oberlippe',
                wulstigeUnterlippe: 'Wulstige Unterlippe',
                raueLippen: 'Raue, rissige Lippen',
                undeutlichesSprechen: 'Undeutliches Sprechen',
                naeseln: 'Näseln',
                lautfehlbildungen: 'Lautfehlbildungen',
                zahnKieferfehlstellungen: 'Zahn- und/oder Kieferfehlstellungen/Fehlbildungen'
              };

              return labels[fieldKey] || fieldKey;
            };

            return (
              <Typography key={key} sx={{ ml: 2 }}>• {getLabel(key)}</Typography>
            );
          })}
        </Box>

        {/* Improvements */}
        {completedData.verbesserungen && Object.keys(completedData.verbesserungen).length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>Verbesserungen durch OSP</Typography>
            {Object.entries(completedData.verbesserungen)
              .filter(([bereich, bewertung]) => bereich && bereich.trim() !== '' && bewertung && bewertung > 0)
              .map(([bereich, bewertung]) => {
                const bewertungsLabels = {
                  1: 'Stimme gar nicht zu',
                  2: 'Stimme nicht zu',
                  3: 'Teils/Teils',
                  4: 'Stimme zu',
                  5: 'Stimme voll zu'
                };
                return (
                  <Typography key={bereich} sx={{ ml: 2 }}>
                    • <strong>{bereich}:</strong> {bewertung}/5 Punkte ({bewertungsLabels[bewertung] || 'Unbekannt'})
                  </Typography>
                );
              })}
          </Box>
        )}

        {/* Final ratings */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>Abschlussbewertung</Typography>
          <Typography sx={{ ml: 2 }}>
            <strong>Gesamtentwicklung:</strong> {completedData.gesamtentwicklung || '-'}
          </Typography>
          <Typography sx={{ ml: 2 }}>
            <strong>Handhabung:</strong> {completedData.handhabung || '-'}
          </Typography>
          <Typography sx={{ ml: 2 }}>
            <strong>Anwendung im Alltag:</strong> {completedData.anwendungAlltag || '-'}
          </Typography>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #ccc', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Fragebogen ausgefüllt am: {new Date().toLocaleDateString('de-DE')}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Formular-Änderungen verarbeiten
  const handleChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Zahlenfelder: Feld darf beim Tippen leer sein (sonst springt es sofort auf den Mindestwert
  // zurück und z. B. eine 4 lässt sich nicht eingeben), leer gelassen gilt beim Verlassen der Mindestwert
  const handleZahlChange = (field, raw) => {
    handleChange('personendaten', field, raw === '' ? '' : parseInt(raw, 10));
  };

  const handleZahlBlur = (field, min) => {
    const wert = formData.personendaten[field];
    if (wert === '' || Number.isNaN(wert) || wert < min) {
      handleChange('personendaten', field, min);
    }
  };

  // Checkbox-Änderungen verarbeiten
  const handleCheckboxChange = (section, field) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: !prev[section][field]
      }
    }));
  };

  // Verbesserungswerte verarbeiten
  const handleVerbesserungChange = (grund, value) => {
    setFormData(prev => ({
      ...prev,
      verbesserungen: {
        ...prev.verbesserungen,
        [grund]: value
      }
    }));
  };

  // Navigation zwischen den Schritten
  const handleNext = async () => {
    // Speichern des aktuellen Fortschritts
    if (uuid && sessionId) {
      try {
        await api.post(`/api/sessions/save/${uuid}/${sessionId}`, { formData });
      } catch (err) {
        console.error('Fehler beim Speichern des Fortschritts:', err);
      }
    }
    
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  // Formular absenden
  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      console.log("Formular wird abgesendet:", formData);
      
      // Senden der Formulardaten direkt, ohne Verpackung in einem formData-Objekt
      const response = await api.post(`/api/questionnaires/submit/${uuid}/${sessionId}`, formData);
      
      console.log("Server-Antwort:", response.data);
      setIsSubmitted(true);
    } catch (err) {
      console.error('Fehler beim Absenden des Fragebogens:', err);
      setError('Beim Absenden des Fragebogens ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render-Funktionen für die verschiedenen Schritte
  const renderPersonalData = () => (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Name (freiwillig)"
            fullWidth
            value={formData.personendaten.name}
            onChange={(e) => handleChange('personendaten', 'name', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Geschlecht</InputLabel>
            <Select
              value={formData.personendaten.geschlecht}
              onChange={(e) => handleChange('personendaten', 'geschlecht', e.target.value)}
              label="Geschlecht"
            >
              <MenuItem value="m">Männlich</MenuItem>
              <MenuItem value="w">Weiblich</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Diagnose"
            fullWidth
            value={formData.personendaten.diagnose}
            onChange={(e) => handleChange('personendaten', 'diagnose', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
            <DatePicker
              label="Tragezeit von"
              value={formData.personendaten.tragezeitVon}
              onChange={(date) => handleChange('personendaten', 'tragezeitVon', date)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
            <DatePicker
              label="Tragezeit bis"
              value={formData.personendaten.tragezeitBis}
              onChange={(date) => handleChange('personendaten', 'tragezeitBis', date)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Anzahl der eingesetzten Stimulationsplatten"
            type="number"
            fullWidth
            value={formData.personendaten.anzahlPlatten}
            onChange={(e) => handleZahlChange('anzahlPlatten', e.target.value)}
            onBlur={() => handleZahlBlur('anzahlPlatten', 0)}
            InputProps={{ inputProps: { min: 0 } }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Die wievielte Platte ist es bzw. wie viele Platten hatten Sie schon?"
            type="number"
            fullWidth
            value={formData.personendaten.plattenNummer}
            onChange={(e) => handleZahlChange('plattenNummer', e.target.value)}
            onBlur={() => handleZahlBlur('plattenNummer', 1)}
            InputProps={{ inputProps: { min: 1 } }}
            helperText="Bitte geben Sie die Nummer der aktuellen Platte ein"
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderGruende = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        1. Grund für den Einsatz einer OSP (Orale Stimulationsplatte)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        OSP = Orale Stimulationsplatte
      </Typography>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.gruende.offeneMundhaltung}
              onChange={() => handleCheckboxChange('gruende', 'offeneMundhaltung')}
            />
          }
          label="Offene Mundhaltung"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.gruende.mundatmung}
              onChange={() => handleCheckboxChange('gruende', 'mundatmung')}
            />
          }
          label="Mundatmung"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.gruende.schnarchen}
              onChange={() => handleCheckboxChange('gruende', 'schnarchen')}
            />
          }
          label="Schnarchen"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.gruende.zaehneKnirschen}
              onChange={() => handleCheckboxChange('gruende', 'zaehneKnirschen')}
            />
          }
          label="Zähneknirschen"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.gruende.schwacheLippen}
              onChange={() => handleCheckboxChange('gruende', 'schwacheLippen')}
            />
          }
          label="Schwache Lippen"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.gruende.verkuerzteOberlippe}
              onChange={() => handleCheckboxChange('gruende', 'verkuerzteOberlippe')}
            />
          }
          label="Verkürzte Oberlippe"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.gruende.wulstigeUnterlippe}
              onChange={() => handleCheckboxChange('gruende', 'wulstigeUnterlippe')}
            />
          }
          label="Wulstige Unterlippe"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.gruende.raueLippen}
              onChange={() => handleCheckboxChange('gruende', 'raueLippen')}
            />
          }
          label="Raue, rissige Lippen"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.gruende.undeutlichesSprechen}
              onChange={() => handleCheckboxChange('gruende', 'undeutlichesSprechen')}
            />
          }
          label="Undeutliches Sprechen"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.gruende.naeseln}
              onChange={() => handleCheckboxChange('gruende', 'naeseln')}
            />
          }
          label="Näseln"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.gruende.lautfehlbildungen}
              onChange={() => handleCheckboxChange('gruende', 'lautfehlbildungen')}
            />
          }
          label="Lautfehlbildungen"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.gruende.zahnKieferfehlstellungen}
              onChange={() => handleCheckboxChange('gruende', 'zahnKieferfehlstellungen')}
            />
          }
          label="Zahn- und/oder Kieferfehlstellungen/Fehlbildungen"
        />
      </FormGroup>
    </Box>
  );

  const renderAngewohnheitenLippeneinzug = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        2. Schädliche Angewohnheiten
      </Typography>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.angewohnheiten.daumenlutschen}
              onChange={() => handleCheckboxChange('angewohnheiten', 'daumenlutschen')}
            />
          }
          label="Daumenlutschen"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.angewohnheiten.schnullergebrauch}
              onChange={() => handleCheckboxChange('angewohnheiten', 'schnullergebrauch')}
            />
          }
          label="Schnullergebrauch"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.angewohnheiten.fingernaegel}
              onChange={() => handleCheckboxChange('angewohnheiten', 'fingernaegel')}
            />
          }
          label="Fingernägelkauen"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.angewohnheiten.lippenbeissen}
              onChange={() => handleCheckboxChange('angewohnheiten', 'lippenbeissen')}
            />
          }
          label="Lippenbeißen"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.angewohnheiten.saugenZunge}
              onChange={() => handleCheckboxChange('angewohnheiten', 'saugenZunge')}
            />
          }
          label="Saugen an der Zunge"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.angewohnheiten.saugenWangen}
              onChange={() => handleCheckboxChange('angewohnheiten', 'saugenWangen')}
            />
          }
          label="Saugen an den Wangen"
        />
      </FormGroup>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>
        3. Lippeneinzug
      </Typography>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.lippeneinzug.unterlippe}
              onChange={() => handleCheckboxChange('lippeneinzug', 'unterlippe')}
            />
          }
          label="Unterlippe"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.lippeneinzug.oberlippe}
              onChange={() => handleCheckboxChange('lippeneinzug', 'oberlippe')}
            />
          }
          label="Oberlippe"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.lippeneinzug.lippenbeissen}
              onChange={() => handleCheckboxChange('lippeneinzug', 'lippenbeissen')}
            />
          }
          label="Lippenbeißen"
        />
      </FormGroup>
    </Box>
  );

  const renderZungenposition = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        4. Zungenposition und Bewegung
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        OK = Oberkiefer, UK = Unterkiefer
      </Typography>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.zungenposition.zungenTieflage}
              onChange={() => handleCheckboxChange('zungenposition', 'zungenTieflage')}
            />
          }
          label="Zungentieflage"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.zungenposition.zungenEinlagerungUkOk}
              onChange={() => handleCheckboxChange('zungenposition', 'zungenEinlagerungUkOk')}
            />
          }
          label="Zungeneinlagerungen zwischen Oberkiefer und Unterkiefer"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.zungenposition.zungenEinlagerungLippen}
              onChange={() => handleCheckboxChange('zungenposition', 'zungenEinlagerungLippen')}
            />
          }
          label="Zungeneinlagerung zwischen den Lippen"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.zungenposition.zungendruckSchluckenOk}
              onChange={() => handleCheckboxChange('zungenposition', 'zungendruckSchluckenOk')}
            />
          }
          label="Zungenbewegung beim Schlucken gegen den OK"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.zungenposition.zungendruckOkUk}
              onChange={() => handleCheckboxChange('zungenposition', 'zungendruckOkUk')}
            />
          }
          label="Zungenbewegung zwischen OK und UK"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.zungenposition.zwischenLippen}
              onChange={() => handleCheckboxChange('zungenposition', 'zwischenLippen')}
            />
          }
          label="Zungenbewegung gegen den UK"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.zungenposition.zungendruckSprechenUk}
              onChange={() => handleCheckboxChange('zungenposition', 'zungendruckSprechenUk')}
            />
          }
          label="Zungenbewegung zwischen die Lippen"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.zungenposition.zungendruckOkLippen}
              onChange={() => handleCheckboxChange('zungenposition', 'zungendruckOkLippen')}
            />
          }
          label="Zungenbewegungen beim Sprechen gegen den OK gerichtet"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.zungenposition.gegenUk}
              onChange={() => handleCheckboxChange('zungenposition', 'gegenUk')}
            />
          }
          label="Zungenbewegungen beim Sprechen gegen den UK gerichtet"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.zungenposition.zwischenOkUk}
              onChange={() => handleCheckboxChange('zungenposition', 'zwischenOkUk')}
            />
          }
          label="Zungenbewegungen beim Sprechen zwischen den OK und UK gerichtet"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.zungenposition.zwischenLippenSprechen}
              onChange={() => handleCheckboxChange('zungenposition', 'zwischenLippenSprechen')}
            />
          }
          label="Zungenbewegungen beim Sprechen zwischen die Lippen gerichtet"
        />
      </FormGroup>
    </Box>
  );

  const renderNahrungZungenbewegung = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        5. Nahrungsaufnahme
      </Typography>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.nahrungsaufnahme.babyflasche}
              onChange={() => handleCheckboxChange('nahrungsaufnahme', 'babyflasche')}
            />
          }
          label="Babyflasche"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.nahrungsaufnahme.trinkenStrohalm}
              onChange={() => handleCheckboxChange('nahrungsaufnahme', 'trinkenStrohalm')}
            />
          }
          label="Trinken aus Strohhalm und Zapfenbechern oder Flaschen"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.nahrungsaufnahme.breiigeNahrung}
              onChange={() => handleCheckboxChange('nahrungsaufnahme', 'breiigeNahrung')}
            />
          }
          label="Breiige Nahrung"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.nahrungsaufnahme.weichereNahrung}
              onChange={() => handleCheckboxChange('nahrungsaufnahme', 'weichereNahrung')}
            />
          }
          label="Weichere Nahrung bevorzugt"
        />
      </FormGroup>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>
        6. Zungenbewegung beim Kauen
      </Typography>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.zungenbewegung.schmatzen}
              onChange={() => handleCheckboxChange('zungenbewegung', 'schmatzen')}
            />
          }
          label="Schmatzen beim Essen"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.zungenbewegung.nahrungAustritt}
              onChange={() => handleCheckboxChange('zungenbewegung', 'nahrungAustritt')}
            />
          }
          label="Nahrung tritt beim Essen aus dem Mund aus bzw. wird oft nach vorne geschoben"
        />
      </FormGroup>
    </Box>
  );

  const renderArtikulationsmerkmale = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        7. Lautfehlbildungen
      </Typography>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.artikulationsmerkmale.stimmklangNasal}
              onChange={() => handleCheckboxChange('artikulationsmerkmale', 'stimmklangNasal')}
            />
          }
          label="Stimmklang nasal"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.artikulationsmerkmale.stimmklangHeiser}
              onChange={() => handleCheckboxChange('artikulationsmerkmale', 'stimmklangHeiser')}
            />
          }
          label="Stimmklang heiser"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.artikulationsmerkmale.insgesamtUndeutlich}
              onChange={() => handleCheckboxChange('artikulationsmerkmale', 'insgesamtUndeutlich')}
            />
          }
          label="Insgesamt undeutlich"
        />
      </FormGroup>
    </Box>
  );

  const renderVerbesserungen = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Verbesserung durch das Tragen einer OSP in folgenden Bereichen
      </Typography>
      
      {verbesserungsOptionen.length === 0 ? (
        <Alert severity="info">
          Bitte wählen Sie zuerst Gründe für den Einsatz einer OSP aus.
        </Alert>
      ) : (
        <>
          {/* Legend for desktop */}
          <Box sx={{ display: { xs: 'none', sm: 'block' }, mb: 2 }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={4}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Bereich
                </Typography>
              </Grid>
              {bewertungsSkala.map((option) => (
                <Grid item xs={1.6} key={option.wert}>
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                      {option.wert}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem' }}>
                      {option.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {verbesserungsOptionen.map((grund) => (
            <Box key={grund} sx={{ mb: 3 }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle1">
                    {grund}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <RadioGroup
                    value={formData.verbesserungen[grund] || 0}
                    onChange={(e) => handleVerbesserungChange(grund, parseInt(e.target.value))}
                    sx={{ 
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 0, sm: 1 }
                    }}
                  >
                    {bewertungsSkala.map((option) => (
                      <FormControlLabel
                        key={option.wert}
                        value={option.wert}
                        control={<Radio size="small" />}
                        label={
                          <Box sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {option.wert}
                            </Typography>
                            <Typography variant="caption" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                              {option.label}
                            </Typography>
                          </Box>
                        }
                        sx={{ 
                          mb: { xs: 1, sm: 0 },
                          flex: { sm: 1 },
                          justifyContent: { sm: 'center' }
                        }}
                      />
                    ))}
                  </RadioGroup>
                </Grid>
              </Grid>
            </Box>
          ))}
        </>
      )}
    </Box>
  );

  const renderAbschluss = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Wie hat sich der Einsatz einer OSP insgesamt auf die Entwicklung Ihres Kindes ausgewirkt?
      </Typography>
      <RadioGroup
        value={formData.personendaten.gesamtentwicklung || ""}
        onChange={(e) => handleChange('personendaten', 'gesamtentwicklung', e.target.value)}
        sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 1
        }}
      >
        <FormControlLabel value="gar_nicht" control={<Radio />} label="Gar nicht" />
        <FormControlLabel value="leicht" control={<Radio />} label="Leicht" />
        <FormControlLabel value="gut" control={<Radio />} label="Gut" />
        <FormControlLabel value="sehr_gut" control={<Radio />} label="Sehr gut" />
      </RadioGroup>
  
      <Divider sx={{ my: 3 }} />
  
      <Typography variant="h6" gutterBottom>
        Wie empfinden Sie die Handhabung einer OSP?
      </Typography>
      <RadioGroup
        value={formData.personendaten.handhabung || ""}
        onChange={(e) => handleChange('personendaten', 'handhabung', e.target.value)}
        sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 1
        }}
      >
        <FormControlLabel value="schwierig" control={<Radio />} label="Schwierig" />
        <FormControlLabel value="unterschiedlich" control={<Radio />} label="Unterschiedlich" />
        <FormControlLabel value="leicht" control={<Radio />} label="Leicht" />
        <FormControlLabel value="problemlos" control={<Radio />} label="Problemlos" />
      </RadioGroup>
  
      <Divider sx={{ my: 3 }} />
  
      <Typography variant="h6" gutterBottom>
        Wie ist die Anwendung einer OSP im Alltag nach einer Eingewöhnungszeit?
      </Typography>
      <RadioGroup
        value={formData.personendaten.anwendungAlltag || ""}
        onChange={(e) => handleChange('personendaten', 'anwendungAlltag', e.target.value)}
        sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 1
        }}
      >
        <FormControlLabel value="schwierig" control={<Radio />} label="Schwierig" />
        <FormControlLabel value="unterschiedlich" control={<Radio />} label="Unterschiedlich" />
        <FormControlLabel value="leicht" control={<Radio />} label="Leicht" />
        <FormControlLabel value="problemlos" control={<Radio />} label="Problemlos" />
      </RadioGroup>

    </Box>
  );

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderPersonalData();
      case 1:
        return renderGruende();
      case 2:
        return renderAngewohnheitenLippeneinzug();
      case 3:
        return renderZungenposition();
      case 4:
        return renderNahrungZungenbewegung();
      case 5:
        return renderArtikulationsmerkmale();
      case 6:
        return renderVerbesserungen();
      case 7:
        return renderAbschluss();
      default:
        return 'Unbekannter Schritt';
    }
  };

  if (error) {
    if (sessionStatus === 'completed' && completedData) {
      // Show completed questionnaire with PDF download option
      return (
        <Container maxWidth="md">
          <Paper sx={{ p: 4, mt: 4 }}>
            <Alert severity="success" sx={{ mb: 3, '& .MuiAlert-icon': { fontSize: '2rem' } }}>
              <Typography variant="h6" gutterBottom>
                Fragebogen bereits ausgefüllt
              </Typography>
              <Typography variant="body1">
                {error}
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Button
                variant="contained"
                startIcon={<PdfIcon />}
                onClick={handlePatientPDFDownload}
                size="large"
                sx={{
                  backgroundColor: '#1976d2',
                  '&:hover': { backgroundColor: '#1565c0' }
                }}
              >
                Fragebogen als PDF herunterladen
              </Button>
            </Box>

            {/* Render the completed questionnaire for PDF generation */}
            {renderCompletedQuestionnaire()}
          </Paper>
        </Container>
      );
    }

    // Default error view for other cases
    return (
      <Container maxWidth="md">
        <Paper sx={{ p: 4, mt: 4 }}>
          <Alert
            severity="error"
            sx={{
              '& .MuiAlert-icon': {
                fontSize: '2rem'
              }
            }}
          >
            <Typography variant="h6" gutterBottom>
              Hinweis
            </Typography>
            <Typography variant="body1">
              {error}
            </Typography>
          </Alert>

          {sessionStatus === 'expired' && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                Bitte wenden Sie sich an Ihren Arzt oder Therapeuten, um einen neuen Link zu erhalten.
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>
    );
  }

  if (isSubmitted) {
    return (
      <Container maxWidth="md">
        <Paper sx={{ p: 4, mt: 4 }}>
          <Alert severity="success">
            Vielen Dank für Ihre Teilnahme! Ihre Antworten wurden erfolgreich übermittelt.
          </Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, mt: { xs: 2, md: 4 } }}>
        {/* Institute Header */}
        <Box
          sx={{
            textAlign: 'center',
            mb: 4,
            p: 3,
            backgroundColor: '#f8f8f8',
            borderRadius: '8px',
            border: '1px solid #e2e2e2'
          }}
        >
          <Box sx={{ mb: 2 }}>
            <img
              src="https://isst-unna.de/wp-content/uploads/2022/09/isst-logo-w.png"
              alt={instituteConfig ? `${instituteConfig.name} Logo` : "ISST Logo"}
              style={{
                maxWidth: '200px',
                height: 'auto',
                display: 'block',
                margin: '0 auto'
              }}
            />
          </Box>
          {instituteConfig && (
            <>
              <Typography
                variant="h5"
                sx={{
                  color: '#dd7017',
                  margin: 0,
                  fontSize: { xs: '1.1rem', sm: '1.3rem' },
                  fontWeight: 'bold'
                }}
              >
                {instituteConfig.name} - {instituteConfig.full_name}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: '#444444',
                  margin: '5px 0',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  fontWeight: 'normal'
                }}
              >
                {instituteConfig.subtitle}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  color: '#888888',
                  margin: '5px 0'
                }}
              >
                {instituteConfig.description}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.8rem' },
                  color: '#888888',
                  mt: 2
                }}
              >
                Inhaberin: {instituteConfig.owner}<br />
                wissenschaftl. Leitung: {instituteConfig.scientific_lead}
              </Typography>
            </>
          )}
        </Box>

        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' } }}
        >
          Fragebogen: Einsatz und Effektivität einer OSP
        </Typography>
        <Typography 
          variant="subtitle1" 
          align="center" 
          gutterBottom
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          Bei Kindern im Alter von 1-14 Jahren
        </Typography>
        <Typography paragraph sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          Vielen Dank, dass Sie sich die Zeit nehmen, den Fragebogen auszufüllen. Bitte beantworten Sie nur die untenstehenden Fragen. Die Ergebnisse werden anonym und vertrauensvoll ausgewertet. Wir freuen uns über Ihre Teilnahme.
        </Typography>

        {/* Desktop Stepper */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mt: 3, mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Mobile Progress */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box sx={{ width: '100%', mr: 1 }}>
              <LinearProgress 
                variant="determinate" 
                value={(activeStep / (steps.length - 1)) * 100} 
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            <Box sx={{ minWidth: 35 }}>
              <Typography variant="body2" color="text.secondary">
                {`${Math.round((activeStep / (steps.length - 1)) * 100)}%`}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" align="center">
            Schritt {activeStep + 1} von {steps.length}
          </Typography>
          <Typography variant="h6" align="center" sx={{ mt: 1 }}>
            {steps[activeStep]}
          </Typography>
        </Box>

        {getStepContent(activeStep)}

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          mt: 4,
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' }
        }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            fullWidth={isMobile}
            sx={{ order: { xs: 2, sm: 1 } }}
          >
            Zurück
          </Button>
          
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
              fullWidth={isMobile}
              size="large"
              sx={{
                order: { xs: 1, sm: 2 },
                backgroundColor: '#dd7017',
                '&:hover': {
                  backgroundColor: '#ff901a'
                }
              }}
            >
              {isSubmitting ? 'Wird gesendet...' : 'Absenden'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              fullWidth={isMobile}
              size="large"
              sx={{
                order: { xs: 1, sm: 2 },
                backgroundColor: '#dd7017',
                '&:hover': {
                  backgroundColor: '#ff901a'
                }
              }}
            >
              Weiter
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default OSPFragebogen;